import { useDeferredValue } from 'react';

import { useAppSelector } from '@store/hooks';
import type { UseDeferredStateOptions } from '@hooks/types';

export const useDeferredIsAuthenticated = () => {
  const isAuthenticated = useAppSelector(state => state.auth.isAuthenticated);
  const deferredIsAuthenticated = useDeferredValue(isAuthenticated);

  return {
    isAuthenticated,
    deferredIsAuthenticated,
  };
};

export type UseIsAuthenticatedOptions = UseDeferredStateOptions;

/**
 * A safety wrapper for authentication state from the store, which uses a
 * deferred value to accommodate side effects of the state changing.
 *
 * De-authentication is handled immediately, while fresh authentication is
 * deferred an extra cycle to allow side effects and transitions to settle.
 *
 * If you need the eager state, pass `deferred: false` to the options.
 */
export const useIsAuthenticated = ({
  deferred = true,
}: UseIsAuthenticatedOptions = {}) => {
  const { isAuthenticated, deferredIsAuthenticated } =
    useDeferredIsAuthenticated();

  return deferred
    ? isAuthenticated && deferredIsAuthenticated
    : isAuthenticated;
};

export default useIsAuthenticated;
